/**
 * 智能体团队HTTP API服务 (V2)
 *
 * 封装智能体团队相关的REST API调用，包括会话管理、消息发送（SSE流式响应）和配置管理。
 * 使用 HttpOnly Cookie 进行认证，浏览器自动携带 Token。
 */

import type {
  AgentTeamSession,
  SessionCreateRequest,
  SessionDetailResponse,
  SessionListResponse,
  SendMessageRequest,
  RoleConfig,
  ConfigUpdateRequest,
  ConfigResetRequest,
  ConfigListResponse,
  SSEEvent,
} from '../types/agentTeam';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

// ==================== 错误类定义 ====================

export class HttpError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

export class UnauthorizedError extends HttpError {
  constructor(message: string = '未授权，请重新登录') {
    super(message, 401, 'UNAUTHORIZED');
    this.name = 'UnauthorizedError';
  }
}

export class NotFoundError extends HttpError {
  constructor(message: string = '资源不存在') {
    super(message, 404, 'NOT_FOUND');
    this.name = 'NotFoundError';
  }
}

export class ForbiddenError extends HttpError {
  constructor(message: string = '没有权限执行此操作') {
    super(message, 403, 'FORBIDDEN');
    this.name = 'ForbiddenError';
  }
}

export class ValidationError extends HttpError {
  constructor(message: string) {
    super(message, 400, 'VALIDATION_ERROR');
    this.name = 'ValidationError';
  }
}

export class NetworkError extends Error {
  constructor(message: string = '网络连接失败，请检查网络') {
    super(message);
    this.name = 'NetworkError';
  }
}

// ==================== 配置 ====================

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504];

// ==================== 辅助函数 ====================

function getHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
  };
}

async function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function handleResponseError(response: Response): Promise<never> {
  let errorMessage = '请求失败';

  try {
    const data = await response.json();
    errorMessage = data.detail || data.message || errorMessage;
  } catch {
    // ignore
  }

  switch (response.status) {
    case 401:
      throw new UnauthorizedError(errorMessage);
    case 403:
      throw new ForbiddenError(errorMessage);
    case 404:
      throw new NotFoundError(errorMessage);
    case 400:
      throw new ValidationError(errorMessage);
    default:
      throw new HttpError(errorMessage, response.status);
  }
}

function shouldRetry(error: unknown, attempt: number): boolean {
  if (attempt >= MAX_RETRIES) return false;

  if (error instanceof NetworkError) return true;

  if (error instanceof HttpError) {
    return RETRYABLE_STATUS_CODES.includes(error.status);
  }

  return false;
}

async function fetchWithRetry<T>(
  url: string,
  options: RequestInit,
  attempt: number = 1
): Promise<T> {
  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof TypeError) {
      throw new NetworkError();
    }

    if (shouldRetry(error, attempt)) {
      await delay(RETRY_DELAY * attempt);
      return fetchWithRetry<T>(url, options, attempt + 1);
    }

    throw error;
  }
}

async function* parseSSEStream(
  response: Response
): AsyncGenerator<SSEEvent> {
  if (!response.body) {
    throw new NetworkError('响应体为空');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      let currentEvent = '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
          continue;
        }

        if (line.startsWith('data: ')) {
          const dataStr = line.slice(6).trim();
          try {
            const data = JSON.parse(dataStr);
            yield {
              event: currentEvent || data.type || 'message',
              data,
            };
            currentEvent = '';
          } catch {
            // ignore parse errors
          }
        }
      }
    }

    if (buffer.trim()) {
      const lines = buffer.split('\n');
      let currentEvent = '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
          continue;
        }
        if (line.startsWith('data: ')) {
          const dataStr = line.slice(6).trim();
          try {
            const data = JSON.parse(dataStr);
            yield {
              event: currentEvent || data.type || 'message',
              data,
            };
          } catch {
            // ignore parse errors
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// ==================== 会话 API ====================

export class AgentTeamApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  async *createSessionStream(
    request: SessionCreateRequest,
    signal?: AbortSignal
  ): AsyncGenerator<SSEEvent> {
    const response = await fetch(`${this.baseUrl}/api/agent-team/v2/sessions`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(request),
      credentials: 'include',
      signal,
    });

    if (!response.ok) {
      await handleResponseError(response);
    }

    yield* parseSSEStream(response);
  }

  async *sendMessageStream(
    sessionId: string,
    request: SendMessageRequest,
    signal?: AbortSignal
  ): AsyncGenerator<SSEEvent> {
    const response = await fetch(
      `${this.baseUrl}/api/agent-team/v2/sessions/${sessionId}/message`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(request),
        credentials: 'include',
        signal,
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    yield* parseSSEStream(response);
  }

  async getSession(
    sessionId: string,
    options?: {
      page?: number;
      pageSize?: number;
    }
  ): Promise<SessionDetailResponse> {
    const params = new URLSearchParams();
    if (options?.page !== undefined) {
      params.set('page', String(options.page));
    }
    if (options?.pageSize !== undefined) {
      params.set('page_size', String(options.pageSize));
    }

    const queryString = params.toString();
    const url = `${this.baseUrl}/api/agent-team/v2/sessions/${sessionId}${queryString ? `?${queryString}` : ''}`;

    return fetchWithRetry<SessionDetailResponse>(url, {
      method: 'GET',
      headers: getHeaders(),
    });
  }

  async listSessions(options?: {
    workspaceId?: number;
    status?: string;
    page?: number;
    pageSize?: number;
  }): Promise<SessionListResponse> {
    const params = new URLSearchParams();
    if (options?.workspaceId !== undefined) {
      params.set('workspace_id', String(options.workspaceId));
    }
    if (options?.status) {
      params.set('status', options.status);
    }

    const queryString = params.toString();
    const url = `${this.baseUrl}/api/agent-team/v2/sessions${queryString ? `?${queryString}` : ''}`;

    return fetchWithRetry<SessionListResponse>(url, {
      method: 'GET',
      headers: getHeaders(),
    });
  }

  async endSession(sessionId: string): Promise<AgentTeamSession> {
    return fetchWithRetry<AgentTeamSession>(
      `${this.baseUrl}/api/agent-team/v2/sessions/${sessionId}/end`,
      {
        method: 'POST',
        headers: getHeaders(),
      }
    );
  }

  async getSessionTasks(sessionId: string): Promise<{ tasks: any[]; dispatches: any[]; total_tasks: number; total_dispatches: number }> {
    return fetchWithRetry(
      `${this.baseUrl}/api/agent-team/v2/sessions/${sessionId}/tasks`,
      {
        method: 'GET',
        headers: getHeaders(),
      }
    );
  }

  async getExecutionStatus(sessionId: string): Promise<{ session_id: string; is_executing: boolean; execution_state: Record<string, unknown> }> {
    return fetchWithRetry(
      `${this.baseUrl}/api/agent-team/v2/sessions/${sessionId}/execution-status`,
      {
        method: 'GET',
        headers: getHeaders(),
      }
    );
  }

  async cancelExecution(sessionId: string): Promise<{ session_id: string; cancelled: boolean }> {
    return fetchWithRetry(
      `${this.baseUrl}/api/agent-team/v2/sessions/${sessionId}/cancel`,
      {
        method: 'POST',
        headers: getHeaders(),
      }
    );
  }

  async *subscribeProgressStream(
    sessionId: string,
    signal?: AbortSignal
  ): AsyncGenerator<SSEEvent> {
    const response = await fetch(
      `${this.baseUrl}/api/agent-team/v2/sessions/${sessionId}/progress-stream`,
      {
        method: 'GET',
        credentials: 'include',
        signal,
      }
    );

    if (!response.ok) {
      await handleResponseError(response);
    }

    yield* parseSSEStream(response);
  }

  // ==================== 配置管理 ====================

  async getConfig(workspaceId: number): Promise<ConfigListResponse> {
    return fetchWithRetry<ConfigListResponse>(
      `${this.baseUrl}/api/agent-team/config/${workspaceId}`,
      {
        method: 'GET',
        headers: getHeaders(),
      }
    );
  }

  async updateConfig(
    workspaceId: number,
    request: ConfigUpdateRequest
  ): Promise<RoleConfig> {
    return fetchWithRetry<RoleConfig>(
      `${this.baseUrl}/api/agent-team/config/${workspaceId}`,
      {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(request),
      }
    );
  }

  async resetConfig(
    workspaceId: number,
    request: ConfigResetRequest
  ): Promise<ConfigListResponse> {
    return fetchWithRetry<ConfigListResponse>(
      `${this.baseUrl}/api/agent-team/config/${workspaceId}/reset`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(request),
      }
    );
  }

  async replyQuestion(
    questionId: string,
    answers: string[][]
  ): Promise<{ success: boolean }> {
    return fetchWithRetry<{ success: boolean }>(
      `${this.baseUrl}/api/chat/sessions/questions/${questionId}/reply`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ question_id: questionId, answers }),
      }
    );
  }

  async dismissQuestion(questionId: string): Promise<{ success: boolean }> {
    return fetchWithRetry<{ success: boolean }>(
      `${this.baseUrl}/api/chat/sessions/questions/${questionId}/dismiss`,
      {
        method: 'POST',
        headers: getHeaders(),
      }
    );
  }
}

// ==================== 单例实例 ====================

let apiInstance: AgentTeamApiService | null = null;

export function getAgentTeamApi(): AgentTeamApiService {
  if (!apiInstance) {
    apiInstance = new AgentTeamApiService();
  }
  return apiInstance;
}

export { AgentTeamApiService as AgentTeamApi };
